import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Mail, Phone, User, MapPin, Sparkles, Lock } from 'lucide-react';

const LeadCaptureModal = ({ isOpen, onClose, onSubmit, quoteData }) => {
  const [formData, setFormData] = useState({
    name: quoteData?.customerName || '',
    email: '',
    phone: '',
    address: '',
    agreeToTerms: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    // Optional field, so empty is valid
    if (!phone) return true;
    // Basic phone validation (10+ digits)
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length >= 10;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!formData.agreeToTerms) {
      newErrors.terms = 'Please agree to receive your quote';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    
    // Track conversion event
    if (window.gtag) {
      window.gtag('event', 'lead_capture', {
        event_category: 'conversion',
        event_label: 'quote_download',
        value: quoteData?.total || 0
      });
    }

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Call parent submit handler
    onSubmit(formData);
    setIsSubmitting(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <DialogTitle className="text-2xl">Get Your Professional Quote</DialogTitle>
          </div>
          <DialogDescription className="text-base">
            Enter your details to download your quote as a professional PDF. We'll also send you a copy via email.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Name Field */}
          <div className="space-y-2">
            <Label htmlFor="lead-name" className="flex items-center gap-2">
              <User className="w-4 h-4 text-slate-600" />
              Full Name *
            </Label>
            <Input
              id="lead-name"
              placeholder="John Smith"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                setErrors({ ...errors, name: '' });
              }}
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && <p className="text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* Email Field */}
          <div className="space-y-2">
            <Label htmlFor="lead-email" className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-slate-600" />
              Email Address *
            </Label>
            <Input
              id="lead-email"
              type="email"
              placeholder="john@example.com"
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                setErrors({ ...errors, email: '' });
              }}
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && <p className="text-sm text-red-600">{errors.email}</p>}
          </div>

          {/* Phone Field (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="lead-phone" className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-slate-600" />
              Phone Number (Optional)
            </Label>
            <Input
              id="lead-phone"
              type="tel"
              placeholder="(555) 123-4567"
              value={formData.phone}
              onChange={(e) => {
                setFormData({ ...formData, phone: e.target.value });
                setErrors({ ...errors, phone: '' });
              }}
              className={errors.phone ? 'border-red-500' : ''}
            />
            {errors.phone && <p className="text-sm text-red-600">{errors.phone}</p>}
          </div>

          {/* Address Field (Optional, freeform) */}
          <div className="space-y-2">
            <Label htmlFor="lead-address" className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-slate-600" />
              Service Address (Optional)
            </Label>
            <Input
              id="lead-address"
              type="text"
              placeholder="123 Main St, Springfield, IL 62701"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              data-testid="lead-address-input"
            />
            <p className="text-xs text-slate-500">Will appear on the PDF quote.</p>
          </div>

          {/* Terms Checkbox */}
          <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg border">
            <Checkbox
              id="lead-terms"
              checked={formData.agreeToTerms}
              onCheckedChange={(checked) => {
                setFormData({ ...formData, agreeToTerms: checked });
                setErrors({ ...errors, terms: '' });
              }}
            />
            <div className="flex-1">
              <label htmlFor="lead-terms" className="text-sm text-slate-700 cursor-pointer leading-relaxed">
                I agree to receive my quote via email. No spam, just your professional estimate.
              </label>
              {errors.terms && <p className="text-sm text-red-600 mt-1">{errors.terms}</p>}
            </div>
          </div>

          {/* Security Badge */}
          <div className="flex items-center gap-2 text-sm text-slate-600 bg-green-50 p-3 rounded-lg border border-green-200">
            <Lock className="w-4 h-4 text-green-600" />
            <span>Your information is secure and will never be shared.</span>
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Processing...' : 'Download PDF Quote'}
            </Button>
          </div>

          {/* Pro Version Teaser */}
          <div className="text-center text-sm text-slate-500 pt-2 border-t">
            <p>Want to save unlimited quotes and access history?</p>
            <a href="#" className="text-blue-600 hover:text-blue-700 font-semibold">Upgrade to Pro →</a>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LeadCaptureModal;